import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const DownloadSection = () => {
  const [email, setEmail] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [errors, setErrors] = useState({});

  const handleDownloadRequest = (e) => {
    e?.preventDefault();
    const newErrors = {};

    if (!email) {
      newErrors.email = 'Email address is required';
    } else if (!/\S+@\S+\.\S+/?.test(email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!businessName) {
      newErrors.businessName = 'Business name is required';
    }

    if (Object.keys(newErrors)?.length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});
    // send request to old site's endpoint so downloads are tracked there
    (async () => {
      try {
        const res = await fetch('https://formspree.io/f/support@cultaz.com', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            type: 'download-request',
            email,
            businessName
          })
        });

        if (res.ok) {
          alert(`Download link will be sent to ${email}. Check your inbox.`);
          setEmail('');
          setBusinessName('');
        } else {
          alert('Could not process request. Please try again or contact support.');
        }
      } catch (err) {
        alert('Network error. Please try again later.');
      }
    })();
  };

  const downloadOptions = [
    {
      id: 1,
      version: "Windows 64-bit",
      size: "125 MB",
      icon: "Download",
      recommended: true
    },
    {
      id: 2,
      version: "Windows 32-bit",
      size: "118 MB",
      icon: "Download",
      recommended: false
    }
  ];

  return (
    <section className="py-16 md:py-20 lg:py-24 bg-background">
      <div className="container mx-auto px-6 md:px-8 lg:px-12">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4 md:mb-6">
              Download Cultaz Desktop POS
            </h2>
            <p className="font-body text-base md:text-lg lg:text-xl text-muted-foreground">
              Get started with your offline POS solution today. Free 30-day trial with full features.
            </p>
          </div>

          <div className="bg-card rounded-2xl border border-border shadow-purple-lg p-6 md:p-8 lg:p-10 mb-8 md:mb-10">
            <form onSubmit={handleDownloadRequest} className="space-y-6">
              <Input
                label="Business Email Address"
                type="email"
                placeholder="your.email@business.com"
                value={email}
                onChange={(e) => setEmail(e?.target?.value)}
                error={errors?.email}
                required
                description="We'll send the download link and license key to this email"
              />

              <Input
                label="Business Name"
                type="text"
                placeholder="Your Store Name"
                value={businessName}
                onChange={(e) => setBusinessName(e?.target?.value)}
                error={errors?.businessName}
                required
                description="This will appear on your receipts and reports"
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                {downloadOptions?.map((option) => (
                  <a
                    key={option?.id}
                    href="https://cultaz.com/Cultaz-setup.exe"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="relative p-6 bg-muted rounded-xl border border-border transition-smooth hover:border-primary hover:shadow-purple-md focus:outline-none focus:ring-3 focus:ring-ring focus:ring-offset-3 text-left"
                  >
                    {option?.recommended && (
                      <div className="absolute top-3 right-3 px-3 py-1 bg-primary text-primary-foreground text-xs font-medium rounded-full">
                        Recommended
                      </div>
                    )}
                    <div className="flex items-center gap-3 mb-3">
                      <Icon name={option?.icon} size={24} color="var(--color-primary)" strokeWidth={2} />
                      <span className="font-heading text-lg font-semibold text-foreground">
                        {option?.version}
                      </span>
                    </div>
                    <span className="font-body text-sm text-muted-foreground">
                      File size: {option?.size}
                    </span>
                  </a>
                ))}
              </div>

              <Button
                type="submit"
                variant="default"
                size="lg"
                fullWidth
                iconName="Download"
                iconPosition="left"
              >
                Request Download Link
              </Button>
            </form>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
            <div className="flex items-center gap-3 p-4 bg-card rounded-xl border border-border">
              <Icon name="Shield" size={20} color="var(--color-success)" strokeWidth={2} />
              <span className="font-body text-sm md:text-base text-foreground">Virus-Free Download</span>
            </div>
            <div className="flex items-center gap-3 p-4 bg-card rounded-xl border border-border">
              <Icon name="Clock" size={20} color="var(--color-success)" strokeWidth={2} />
              <span className="font-body text-sm md:text-base text-foreground">30-Day Free Trial</span>
            </div>
            <div className="flex items-center gap-3 p-4 bg-card rounded-xl border border-border">
              <Icon name="Headphones" size={20} color="var(--color-success)" strokeWidth={2} />
              <span className="font-body text-sm md:text-base text-foreground">Free Setup Support</span>
            </div>
          </div>

          <div className="mt-8 md:mt-10 text-center">
            <p className="font-body text-sm md:text-base text-muted-foreground mb-4">
              Need help with installation or have questions?
            </p>
            <Button
              variant="outline"
              size="default"
              iconName="Phone"
              iconPosition="left"
            >
              Contact Support Team
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DownloadSection;